/**
 * A row of tabs that folds its tail into a menu on the right when the row is too narrow, and
 * unfolds it again when there is room.
 *
 * ED, 2026-10-27: "When the screen shrinks, have the menus on the right convert to a right side
 * dropdown dynamically and revert when it expands." A record page's strip was seven tabs; a
 * project's is thirteen, and a row that wraps puts the second line under the first with no sign
 * of which is the strip.
 *
 * HOW IT DECIDES. Every tab is rendered once, invisibly, to learn its natural width; then a
 * ResizeObserver on the row reports the width available, and the tabs are taken in order until
 * the next would not fit beside the "More" button. The ones left over go into the menu. Measured,
 * not guessed from a count, because "Quote Responses (12)" and "Detail" are not the same width.
 *
 * THE SELECTED TAB IS NEVER HIDDEN SILENTLY. If it falls into the overflow, the menu button wears
 * its label and the underline, so the page always shows which tab it is on.
 */
import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Menu, MenuItem} from "@mui/material";
import {ExpandMore} from "@mui/icons-material";

export type OverflowTab = {key: string; label: string};

type Props = {
    tabs: OverflowTab[];
    selected: string;
    onSelect: (key: string) => void;
    /** The page's own tab look, so a folded strip is the same strip. */
    tabStyle: (active: boolean) => React.CSSProperties;
    gap?: number;
    style?: React.CSSProperties;
};

const MORE_WIDTH = 110;

export default function OverflowTabs({tabs, selected, onSelect, tabStyle, gap = 22, style}: Props) {
    const rowRef = useRef<HTMLDivElement | null>(null);
    const measureRef = useRef<HTMLDivElement | null>(null);
    const [widths, setWidths] = useState<Record<string, number>>({});
    const [available, setAvailable] = useState<number>(0);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    // NATURAL WIDTHS, from a hidden copy of the row. Re-measured whenever a label changes
    // (a count moving from 9 to 10 widens a tab).
    const labelsKey = tabs.map(one => `${one.key}=${one.label}`).join("|");
    useLayoutEffect(() => {
        const held = measureRef.current;
        if (!held) return;
        const next: Record<string, number> = {};
        Array.from(held.children).forEach(child => {
            const key = (child as HTMLElement).dataset.key;
            if (key) next[key] = (child as HTMLElement).getBoundingClientRect().width;
        });
        setWidths(next);
    }, [labelsKey]);

    useEffect(() => {
        const row = rowRef.current;
        if (!row) return;
        const read = () => setAvailable(row.getBoundingClientRect().width);
        read();
        const observer = new ResizeObserver(read);
        observer.observe(row);
        return () => observer.disconnect();
    }, []);

    // HOW MANY FIT. The whole row fits with no button, or as many as fit beside the button.
    let shown = tabs.length;
    if (available > 0 && Object.keys(widths).length === tabs.length) {
        const total = tabs.reduce((sum, one) => sum + (widths[one.key] ?? 0), 0)
            + gap * Math.max(0, tabs.length - 1);
        if (total > available) {
            let used = 0;
            shown = 0;
            for (const one of tabs) {
                const width = (widths[one.key] ?? 0) + (shown > 0 ? gap : 0);
                if (used + width + gap + MORE_WIDTH > available) break;
                used += width;
                shown += 1;
            }
        }
    }
    const visible = tabs.slice(0, shown);
    const folded = tabs.slice(shown);
    const foldedSelected = folded.find(one => one.key === selected);

    return (
        <div ref={rowRef} style={{position: "relative", ...style}}>
            {/* The hidden copy, laid out exactly as the row is, so the widths are the row's.
                ITS LABELS ARE DRAWN BY CSS, NOT PUT IN THE DOM: a second "Contracts (1)" in the
                document is a second match for every text locator and screen reader, and the
                first run of the project spec failed on exactly that -- "resolved to 2 elements".
                `content: attr(data-label)` measures the same width and is text to nobody. */}
            <style>{`.overflow-tabs-measure > div::before { content: attr(data-label); }`}</style>
            <div ref={measureRef} aria-hidden className="overflow-tabs-measure"
                 style={{position: "absolute", visibility: "hidden", pointerEvents: "none",
                         display: "flex", gap, whiteSpace: "nowrap", left: 0, top: 0}}>
                {tabs.map(one => (
                    <div key={one.key} data-key={one.key} data-label={one.label}
                         style={tabStyle(one.key === selected)}/>
                ))}
            </div>
            <div data-testid="tab-strip"
                 style={{display: "flex", gap, alignItems: "flex-end", whiteSpace: "nowrap",
                         overflow: "hidden"}}>
                {visible.map(one => (
                    <div key={one.key} style={tabStyle(one.key === selected)}
                         onClick={() => onSelect(one.key)}>
                        {one.label}
                    </div>
                ))}
                {folded.length > 0 && (
                    <div style={{marginLeft: "auto", display: "flex", alignItems: "center", gap: 4,
                                 ...tabStyle(Boolean(foldedSelected))}}
                         onClick={event => setAnchor(event.currentTarget)}>
                        {foldedSelected ? foldedSelected.label : "More"}
                        <ExpandMore style={{fontSize: 16}}/>
                    </div>
                )}
            </div>
            <Menu open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
                  anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                  transformOrigin={{vertical: "top", horizontal: "right"}}>
                {folded.map(one => (
                    <MenuItem key={one.key} selected={one.key === selected} dense
                              onClick={() => { setAnchor(null); onSelect(one.key); }}>
                        {one.label}
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
}
